<template>
  <AssignSamplesView
    v-bind="{ ...$attrs, ...$props }"
    :assay-model-type="assayType"
    :group-id="groupId"
    :sample-type="sampleType"
    :register="() => {}"
    :redirect="false"
    :use-sample-groups="false"
    :error="registerError"
    :has-pooled-samples="hasPooledSamples"
    @update-allow-pooled-samples="(a) => (allowPooledSamples = a)"
  >
    <template #notifications>
      <div
        v-if="registerError"
        class="notification is-fullwidth is-danger pb-2"
      >
        <span class="icon">
          <i class="fa-solid fa-bug" />
        </span>
        <span
          class="is-flex is-flex-direction-column"
          style="text-align: left; padding: 0.2rem 1rem; width: 100%"
        >
          {{ registerError }}
        </span>
      </div>
    </template>
    <template
      #body="{
        metaSample,
        sampleIdentityFields,
        sampleOptions,
        studyOptions,
        dropdownOptions,
        toggleSampleModal,
        openStudyPicker,
      }"
    >
      <SamplesToDatasetsAssigner
        v-if="sampleAssignDatasets.length"
        :datasets-data="sampleAssignDatasets"
        :meta-sample="metaSample"
        :sample-identity-fields="sampleIdentityFields"
        :allow-pooled-samples="allowPooledSamples"
        :sample-options="sampleOptions"
        :study-options="studyOptions"
        :dropdown-options="dropdownOptions"
        :toggle-sample-modal="toggleSampleModal"
        :open-study-picker="openStudyPicker"
        @update-samples="(samples) => (samplesData = samples)"
        @update-datasets="(datasets) => (datasetsData = datasets)"
        @update-has-intracollection-duplication="
          (dup) => (hasPooledSamples = dup)
        "
        @error="(e) => (hasError = e)"
      >
        <template #sample-selector>{{ sampleTypeModel.label }}</template>
      </SamplesToDatasetsAssigner>
    </template>
    <template #footer>
      <div class="is-flex">
        <BaseButton
          v-tooltip="{
            content: `
            <p>Please provide more information about your sample,</p>
            <p>
              e.g. did you associate your sample with a study ?
              Check the <b>Study</b> column
              </p>`,
            html: true,
          }"
          :icon="['fa-solid', 'fa-save']"
          label="Submit"
          variant="primary"
          :outlined="!canRegister"
          :disabled="!canRegister || isRegistering"
          :loading="isRegistering"
          @click="register"
        />
      </div>
    </template>
  </AssignSamplesView>
</template>

<script>
import { get, put } from '@/api'
import { mapGetters, mapState } from 'vuex'
import { uuidv4 } from '@/utils'
import AssignSamplesView from '@/views/data-loader/AssignSamplesView.vue'
import SamplesToDatasetsAssigner from '@/components/data-loader/SamplesToDatasetsAssigner.vue'
export default {
  name: 'AssayAssignSamplesView',
  components: {
    AssignSamplesView,
    SamplesToDatasetsAssigner,
  },
  data() {
    return {
      samplesData: [],
      datasetsData: [],
      isLoading: false,
      isRegistering: false,
      datasetsList: [],
      hasError: false,
      registerError: null,
      allowPooledSamples: false,
      hasPooledSamples: false,
    }
  },
  computed: {
    ...mapState('dataloader/assay', {
      assayData: (state) => state.assayData,
      assayId: (state) => state.assayData?.id?.value || null,
      assayName: (state) => state.assayData?.name?.value?.name || null,
      assayType: (_, getters) => getters.resolvedAssayType,
      sampleType: 'sampleType',
    }),
    ...mapGetters('dataloader/assay', ['sampleTypeModel']),
    ...mapGetters('auth', ['loggedInUserPrimaryGroup']),
    ...mapGetters('dataloader/main', ['studyOptions']),
    apiAssayRegistrationUrl() {
      return `assays/assays/${this.assayId}/register/`
    },
    groupId() {
      return this.loggedInUserPrimaryGroup.id
    },
    sampleAssignDatasets() {
      const uuid = uuidv4()
      return this.datasetsList.map((dataset) => ({
        name: dataset.sample.value,
        collection: uuid,
        collectionName: `${this.assayName} raw data`, //need better name
        datafiles: dataset.datafiles.value,
        sample: null,
        barcode: dataset.barcode,
        owner: dataset.owner.value,
        studies: this.studyOptions,
      }))
    },
    canRegister() {
      return this.datasetsList.length > 0 && this.hasError === false
    },
    sampleDataById() {
      return this.samplesData.reduce((samplesDataById, sampleData) => {
        if (sampleData.id) {
          samplesDataById[sampleData.id] = sampleData
        }
        return samplesDataById
      }, {})
    },
    payload() {
      const datasets = this.datasetsData.map(
        ({ datafiles, owner, studies, samples }) => {
          const sample = samples.pop()
          const sampleName = this.sampleDataById[sample].name
          const isNewSample = this.sampleDataById[sample].new === true
          const sampleBarcode = isNewSample
            ? this.sampleDataById[sample]?.barcode
            : null
          return {
            datafiles: { value: datafiles },
            owner: { value: owner },
            sample: { value: isNewSample ? sampleName : { id: sample } },
            studies: { value: studies.map((s) => ({ id: s.id.value })) },
            barcode: { value: sampleBarcode },
          }
        },
      )
      return {
        datasets,
      }
    },
  },
  methods: {
    async getData() {
      this.isLoading = true
      const response = await get(
        `${this.apiAssayRegistrationUrl}?format=json&response_type=flat`,
      ).catch(this.handleError)
      if (response?.status === 200) this.datasetsList = response.data.datasets
      this.isLoading = false
    },
    handleError(error) {
      this.registerError =
        error.response?.data?.message ||
        error.message ||
        'An error occurred. Please try again.'
      this.$store.commit('notify', {
        message: `Error loading data from server ${this.registerError}`,
        type: 'error',
      })
    },
    async register() {
      this.isRegistering = true
      const registerUrl = `${this.apiAssayRegistrationUrl}?allow_pooled_samples=${this.allowPooledSamples}`
      const response = await put(registerUrl, {
        data: this.payload,
      })
        .catch(this.handleError)
        .finally(() => (this.isRegistering = false))
      if (response?.status === 200 || response?.status === 202) {
        /* Modify current url to add success query param
         * this allows to bypass navguards */
        await this.$router.push({
          ...this.$route,
          query: { success: true },
        })
        this.$store.commit('notify', {
          type: 'success',
          message: 'Successfully registered assay',
        })
        this.$router.push({
          name: 'assayDetail',
          params: {
            id: this.assayId,
            modelType: this.assayType,
          },
        })
      }
    },
  },
  async mounted() {
    await this.getData()
  },
}
</script>
